import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ArrowLeft, Loader2, Download, BookOpen, PenLine, Target, Shield, ChevronDown, ChevronUp, CheckCircle, Heart, Brain, Wind } from "lucide-react";
import { toast } from "sonner";
import { PageSEO } from "@/components/SEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIT_META = {
  "kit-self-care": { icon: Heart, color: "var(--ll-green)", seoTitle: "Self-Care Kit", seoDesc: "Daily self-care planner, guided journaling prompts, habit tracking tools, and relaxation techniques for building sustainable wellness habits." },
  "kit-meditation": { icon: Brain, color: "var(--ll-accent)", seoTitle: "Meditation Kit", seoDesc: "Guided meditation scripts, breathing exercise cards, mindfulness journal prompts, and calm techniques for busy professionals." },
  "kit-stress-relief": { icon: Wind, color: "var(--ll-text-secondary)", seoTitle: "Stress Relief Kit", seoDesc: "Practical stress management tools, grounding exercises, emergency calm guide, and progressive relaxation techniques." },
};

const renderInlineMarkdown = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`b-${i}`} style={{ color: "var(--ll-text)" }}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const GuideContent = ({ content }) => {
  // Split into lines, then render each line based on its prefix
  const lines = content.split("\n");

  return (
    <div className="prose-custom space-y-1">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={`sp-${i}`} className="h-3" />;
        if (trimmed.startsWith("## ")) {
          return <h3 key={`h-${i}`} className="font-serif text-xl font-medium mt-8 mb-3" style={{ color: "var(--ll-text)" }}>{trimmed.replace("## ", "")}</h3>;
        }
        if (trimmed.startsWith("- ")) {
          return (
            <div key={`li-${i}`} className="flex items-start gap-2.5 py-1 ml-1">
              <CheckCircle size={12} className="mt-1 flex-shrink-0" style={{ color: "var(--ll-green)" }} />
              <p className="text-sm leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{renderInlineMarkdown(trimmed.replace("- ", ""))}</p>
            </div>
          );
        }
        if (trimmed.match(/^\d+\./)) {
          const num = trimmed.match(/^(\d+)/)[1];
          return (
            <div key={`ol-${i}`} className="flex items-start gap-3 py-1 ml-1">
              <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-medium mt-0.5" style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{num}</span>
              <p className="text-sm leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{renderInlineMarkdown(trimmed.replace(/^\d+\.\s*/, ""))}</p>
            </div>
          );
        }
        return <p key={`p-${i}`} className="text-sm leading-[1.8]" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{renderInlineMarkdown(trimmed)}</p>;
      })}
    </div>
  );
};

const ExerciseCard = ({ exercise }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--ll-border)" }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left">
        <div>
          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{exercise.name}</p>
          <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{exercise.duration} · {exercise.when}</p>
        </div>
        {open ? <ChevronUp size={16} style={{ color: "var(--ll-text-muted)" }} /> : <ChevronDown size={16} style={{ color: "var(--ll-text-muted)" }} />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <ol className="space-y-2">
            {exercise.steps.map((step, i) => (
              <li key={`step-${exercise.name}-${i}`} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-medium mt-0.5" style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{i + 1}</span>
                <p className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{step}</p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

const JournalWeek = ({ week }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--ll-border)" }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left">
        <div>
          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Week {week.week}: {week.theme}</p>
          <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{week.prompts.length} prompts</p>
        </div>
        {open ? <ChevronUp size={16} style={{ color: "var(--ll-text-muted)" }} /> : <ChevronDown size={16} style={{ color: "var(--ll-text-muted)" }} />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {week.prompts.map((prompt, i) => (
            <div key={`prompt-${week.week}-${i}`} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: "var(--ll-bg-secondary)" }}>
              <span className="text-xs font-medium flex-shrink-0 mt-0.5" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Day {i + 1}</span>
              <p className="text-sm italic" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{prompt}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HabitTracker = ({ habits }) => {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [checked, setChecked] = useState({});
  const toggle = (habit, day) => {
    const key = `${habit}-${day}`;
    setChecked({ ...checked, [key]: !checked[key] });
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="text-left py-2 pr-4 text-xs font-medium" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Habit</th>
            {days.map((d) => <th key={d} className="text-center py-2 px-1 text-[10px] font-medium" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {habits.map((h) => (
            <tr key={h.name}>
              <td className="py-2 pr-4">
                <p className="text-xs font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{h.name}</p>
                <p className="text-[10px]" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{h.target}</p>
              </td>
              {days.map((d) => {
                const key = `${h.name}-${d}`;
                return (
                  <td key={key} className="text-center py-2 px-1">
                    <button onClick={() => toggle(h.name, d)} className="w-6 h-6 rounded-lg transition-all"
                      style={{ background: checked[key] ? "var(--ll-green)" : "var(--ll-bg-secondary)", border: `1px solid ${checked[key] ? "var(--ll-green)" : "var(--ll-border)"}` }}>
                      {checked[key] && <CheckCircle size={12} style={{ color: "white" }} className="mx-auto" />}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SectionRenderer = ({ section }) => {
  const SECTION_ICONS = { guide: BookOpen, prompts: PenLine, exercises: Shield, tracker: Target };
  const Icon = SECTION_ICONS[section.type] || BookOpen;

  return (
    <div data-testid={`kit-section-${section.id}`} className="mb-10">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--ll-bg-secondary)" }}>
          <Icon size={18} style={{ color: "var(--ll-green)" }} />
        </div>
        <h2 className="font-serif text-xl sm:text-2xl font-medium" style={{ color: "var(--ll-text)" }}>{section.title}</h2>
      </div>

      {section.type === "guide" && <GuideContent content={section.content} />}
      {section.type === "exercises" && (
        <div className="space-y-3">{section.exercises.map((ex) => <ExerciseCard key={ex.name} exercise={ex} />)}</div>
      )}
      {section.type === "prompts" && (
        <div className="space-y-3">{section.prompts.map((week) => <JournalWeek key={`week-${week.week}`} week={week} />)}</div>
      )}
      {section.type === "tracker" && <HabitTracker habits={section.habits} />}
    </div>
  );
};

const KitContentPage = () => {
  const { kitId } = useParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [kitData, setKitData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const meta = KIT_META[kitId] || {};
  const KitIcon = meta.icon || Heart;

  useEffect(() => {
    let cancelled = false;
    const fetchPreview = async () => {
      try {
        const res = await axios.get(`${API}/kit/preview/${kitId}`);
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) toast.error("Could not load kit preview.");
      }
    };
    fetchPreview();
    return () => { cancelled = true; };
  }, [kitId]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const accessRes = await axios.post(`${API}/kit/verify-access`, { email });
      if (accessRes.data.access && accessRes.data.accessible_kits.includes(kitId)) {
        const contentRes = await axios.get(`${API}/kit/content/${kitId}`);
        setKitData(contentRes.data.kit);
        setHasAccess(true);
        setActiveSection(contentRes.data.kit.sections[0]?.id);
        toast.success("Access verified! Enjoy your kit.");
      } else if (accessRes.data.access) {
        toast.error("You don't have access to this specific kit.");
      } else {
        toast.error(accessRes.data.message || "No access found.");
      }
    } catch (err) { toast.error("Verification failed."); }
    finally { setLoading(false); }
  };

  const handleExport = () => {
    if (!kitData) return;
    let text = `${kitData.name.toUpperCase()}\n${kitData.tagline}\nThe Lemonade Living Company\n${"=".repeat(50)}\n\n`;
    kitData.sections.forEach((s) => {
      text += `\n${"─".repeat(40)}\n${s.title.toUpperCase()}\n${"─".repeat(40)}\n\n`;
      if (s.content) text += s.content.replace(/## /g, "\n▸ ").replace(/\*\*/g, "") + "\n";
      if (s.exercises) s.exercises.forEach((ex) => { text += `▸ ${ex.name} (${ex.duration})\n  When: ${ex.when}\n  Steps:\n${ex.steps.map((st, i) => `    ${i + 1}. ${st}`).join("\n")}\n\n`; });
      if (s.prompts) s.prompts.forEach((w) => { text += `Week ${w.week}: ${w.theme}\n${w.prompts.map((p, i) => `  Day ${i + 1}: ${p}`).join("\n")}\n\n`; });
      if (s.habits) { text += "Weekly Habit Tracker:\n"; s.habits.forEach((h) => { text += `  □ ${h.name} — ${h.target}\n`; }); }
    });
    text += `\n${"=".repeat(50)}\nwww.thelemonadelivingcompany.com\n`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kitId}-${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!hasAccess) {
    return (
      <main data-testid="kit-content-page" className="pt-20">
        <PageSEO title={meta.seoTitle || "Wellness Kit"} description={meta.seoDesc || ""} path={`/kit/${kitId}`} />
        <section className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs mb-8 hover:opacity-70" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}><ArrowLeft size={12} /> Back to Home</Link>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: "var(--ll-bg-secondary)" }}>
                  <KitIcon size={26} style={{ color: meta.color }} />
                </div>
                <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Wellness Kit</p>
                <h1 className="font-serif text-4xl sm:text-5xl font-light leading-[1.1] mb-4" style={{ color: "var(--ll-text)" }}>{preview?.name || meta.seoTitle}</h1>
                <p className="text-base leading-relaxed mb-8" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{preview?.tagline}</p>
                {preview?.sections && (
                  <div className="space-y-2">
                    <p className="text-xs tracking-[0.2em] uppercase mb-2" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>What's Inside</p>
                    {preview.sections.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
                        <Lock size={12} style={{ color: "var(--ll-text-muted)" }} />
                        <p className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{s.title}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="lg:sticky lg:top-28">
                <div className="p-8 rounded-2xl" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                  <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Access Your Kit</h2>
                  <p className="text-sm mb-6" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Enter the email you used to purchase.</p>
                  <form onSubmit={handleVerify} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Purchase Email</Label>
                      <Input data-testid="kit-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-xl border" style={{ borderColor: "var(--ll-border)" }} />
                    </div>
                    <button type="submit" data-testid="kit-verify-btn" disabled={loading}
                      className="w-full rounded-full px-6 py-3.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5"
                      style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                      {loading ? "Verifying..." : "Unlock My Kit"}
                    </button>
                  </form>
                  <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--ll-border)" }}>
                    <p className="text-xs text-center mb-3" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Haven't purchased yet?</p>
                    <Link to="/#products" className="w-full rounded-full px-6 py-3 text-sm font-medium border flex items-center justify-center hover:-translate-y-0.5 transition-all"
                      style={{ borderColor: "var(--ll-border)", color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                      Purchase Kit — R250
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="kit-content-page" className="pt-20">
      <PageSEO title={kitData?.name || meta.seoTitle} path={`/kit/${kitId}`} />
      <section className="py-8 md:py-12 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-xs flex items-center gap-1 hover:opacity-70" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}><ArrowLeft size={12} /> Home</Link>
              <span style={{ color: "var(--ll-text-muted)" }}>/</span>
              <span className="text-xs font-medium" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{kitData?.name}</span>
            </div>
            <button data-testid="kit-export-btn" onClick={handleExport}
              className="rounded-full px-5 py-2 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
              style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
              <Download size={12} /> Download Kit (TXT)
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden lg:sticky lg:top-28" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                <div className="p-4" style={{ borderBottom: "1px solid var(--ll-border)" }}>
                  <h3 className="font-serif text-base font-medium" style={{ color: "var(--ll-text)" }}>{kitData?.name}</h3>
                  <p className="text-[11px] mt-0.5" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{kitData?.sections.length} sections</p>
                </div>
                {kitData?.sections.map((s) => (
                  <button key={s.id} data-testid={`kit-nav-${s.id}`} onClick={() => setActiveSection(s.id)}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-left transition-all"
                    style={{ background: activeSection === s.id ? "var(--ll-bg-secondary)" : "transparent", borderBottom: "1px solid var(--ll-border)" }}>
                    <p className="text-xs font-medium truncate" style={{ color: activeSection === s.id ? "var(--ll-text)" : "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{s.title}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              {kitData?.sections.filter((s) => s.id === activeSection).map((s) => (
                <SectionRenderer key={s.id} section={s} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default KitContentPage;
